/**
 * Map persisted workbook definitions ↔ Leasing Season Config section drafts.
 * EZOS registry supplies question semantics; definitions store placements.
 */

import type {
    EzosWorkbookField,
    WorkbookBucketConfig,
    WorkbookDefinition,
    WorkbookFieldConfig,
} from './workbook.types'
import { PINNED_WORKBOOK_FIELD_IDS } from './workbookCatalog'
import {
    GENDER_FIELD_ID,
    genderQuestionSpec,
    normalizeGenderQuestionMode,
} from './genderQuestion'
import type {
    WorkbookSectionDraft,
    WorkbookSectionFieldDraft,
    WorkbookSectionsState,
} from '../PipelineFlowDemo/workbookSectionDraft'
import { WORKBOOK_BUCKETS } from '../PipelineFlowDemo/workbookBuckets'

export function definitionToSectionsState(
    definition: WorkbookDefinition | undefined | null,
    registryFields: EzosWorkbookField[] = [],
): WorkbookSectionsState {
    const byId = new Map(registryFields.map((f) => [f.field_id, f]))
    const state: WorkbookSectionsState = {}
    const buckets = definition?.buckets ?? []

    for (const bucket of buckets) {
        state[bucket.bucket_id] = bucketToSectionDraft(bucket, byId)
    }

    // Ensure platform shells exist even if missing from an older definition.
    for (const platform of WORKBOOK_BUCKETS) {
        if (!state[platform.id]) {
            state[platform.id] = {
                bucket_id: platform.id,
                label: platform.label,
                summary: platform.summary,
                kind: 'platform',
                configured: false,
                enabled: platform.id !== 'failed_qualification',
                fields: [],
            }
        }
    }

    return state
}

function bucketToSectionDraft(
    bucket: WorkbookBucketConfig,
    byId: Map<string, EzosWorkbookField>,
): WorkbookSectionDraft {
    const fields: WorkbookSectionFieldDraft[] = (bucket.fields || [])
        .filter((f) => f.enabled !== false)
        .map((f) => placementToFieldDraft(f, byId))

    return {
        bucket_id: bucket.bucket_id,
        label: bucket.label,
        summary: bucket.summary || '',
        kind: bucket.kind === 'custom' ? 'custom' : 'platform',
        configured: bucket.enabled !== false && fields.length > 0,
        enabled: bucket.enabled !== false,
        fields,
    }
}

function placementToFieldDraft(
    field: WorkbookFieldConfig,
    byId: Map<string, EzosWorkbookField>,
): WorkbookSectionFieldDraft {
    const reg = byId.get(field.field_id)
    const isCustom = field.field_id.startsWith('custom_') && !reg
    return {
        field_id: field.field_id,
        question_text:
            reg?.question_text ||
            field.question_text ||
            field.label ||
            field.field_id,
        input_type: reg?.input_type || field.input_type || 'text',
        enabled: true,
        required: !!field.required,
        is_custom: isCustom,
        pinned:
            Boolean(reg?.pinned) ||
            PINNED_WORKBOOK_FIELD_IDS.has(field.field_id),
        ...(reg ? { source_field_id: reg.field_id } : {}),
        options: reg?.options?.length
            ? [...reg.options]
            : field.options
            ? [...field.options]
            : undefined,
        show_if: field.show_if
            ? {
                  depends_on_field_id: field.show_if.depends_on_field_id,
                  equals: field.show_if.equals,
              }
            : undefined,
        ...(field.field_id === GENDER_FIELD_ID
            ? genderQuestionSpec(field.gender_question_mode)
            : {}),
    }
}

export function sectionsStateToDefinition(
    sections: WorkbookSectionsState | undefined,
): WorkbookDefinition {
    const state = sections ?? {}
    const buckets: WorkbookBucketConfig[] = []

    for (const platform of WORKBOOK_BUCKETS) {
        const draft = state[platform.id]
        buckets.push(
            sectionDraftToBucket(
                draft,
                platform.id,
                'platform',
                platform.label,
                platform.summary,
            ),
        )
    }

    const customs = Object.values(state).filter(
        (d): d is WorkbookSectionDraft => !!d && d.kind === 'custom',
    )
    for (const draft of customs.sort((a, b) =>
        a.label.localeCompare(b.label),
    )) {
        buckets.push(
            sectionDraftToBucket(
                draft,
                draft.bucket_id,
                'custom',
                draft.label,
                draft.summary,
            ),
        )
    }

    return {
        version: 2,
        buckets,
        identity_phase_field_ids: [],
    }
}

function sectionDraftToBucket(
    draft: WorkbookSectionDraft | undefined,
    bucketId: string,
    kind: 'platform' | 'custom',
    fallbackLabel: string,
    fallbackSummary: string,
): WorkbookBucketConfig {
    const fields: WorkbookFieldConfig[] = (draft?.fields || [])
        .filter((f) => f.enabled !== false)
        .map((f) => {
            const placement: WorkbookFieldConfig = {
                field_id: f.field_id,
                enabled: true,
                required: !!f.required,
            }
            if (f.show_if) {
                placement.show_if = { ...f.show_if }
            }
            if (f.field_id === GENDER_FIELD_ID) {
                placement.gender_question_mode = normalizeGenderQuestionMode(
                    f.gender_question_mode,
                )
            }
            // Org/custom inline semantics only when not an EZOS registry field.
            if (f.is_custom || f.field_id.startsWith('custom_')) {
                placement.question_text = f.question_text
                placement.label = f.question_text
                placement.input_type = f.input_type
                if (f.options?.length) placement.options = [...f.options]
            }
            return placement
        })

    let enabled = draft?.enabled !== false
    if (bucketId === 'failed_qualification') {
        enabled = draft?.enabled !== false && fields.length > 0
    }

    return {
        bucket_id: bucketId,
        label: draft?.label || fallbackLabel,
        summary: draft?.summary || fallbackSummary || '',
        kind,
        enabled,
        fields,
    }
}

export function ezosFieldsToRegistryMap(
    fields: EzosWorkbookField[],
): Map<string, EzosWorkbookField> {
    return new Map(fields.map((f) => [f.field_id, f]))
}
